import { RubyGemMark } from '../../../components/RubyGemMark';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Ban, CheckCircle2, PackageOpen, RotateCcw, ShieldAlert } from 'lucide-react';
import {
  CANCELLATION_EXITS, settlementTotals,
  type CancellationExit, type CancellationRecord, type SettlementLine, type SettlementTotals,
} from '@platform/shared-types';
import { journeyApi } from '../../../services/journeyApi';
import { hardwareApi, type DeviceUnitRow } from '../../../services/hardwareApi';
import { toastService } from '../../../services/toastService';
import { useDataVersion } from '../../../services/dataChangeBus';
import { RubyLoader } from '../../../components/RubyLoader';

/**
 * THE SETTLEMENT WORKSHEET.
 *
 * Design source: mockups/order-cancellation-refund.html. Same ruby chrome as
 * the rest of super-admin, never purple; the same in-page confirmation
 * pattern as everywhere else in this app — `confirm()` is a silent no-op in
 * the iOS webview, so a cancellation confirmed through one would simply
 * never happen.
 *
 * Every number below the header comes from `settlementTotals`, the SAME
 * function journey-service runs when it stores a settlement. This screen
 * never re-derives a total — a screen that computed its own would eventually
 * disagree with what was actually stored, and the screen is what people trust.
 */

/** Cents in, never a float. Money is integer cents everywhere in this system. */
export const money = (cents: number) => {
  const body = (Math.abs(cents) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${cents < 0 ? '−' : ''}$${body}`;
};

const EXIT_COPY: Record<CancellationExit, { title: string; desc: string }> = {
  penalty: {
    title: 'Charge a penalty',
    desc: 'Hold them to what the policy says. Refund what is refundable, keep what is not.',
  },
  forgive: {
    title: 'Forgive everything pending',
    desc: 'Every penalty to zero and the outstanding balance written off. A gift, recorded as one.',
  },
  solution: {
    title: 'Work out a solution',
    desc: 'A counter-offer that keeps the order alive. Nothing is refunded and nothing is cancelled.',
  },
};

const GROUP_LABEL: Record<string, string> = {
  software: 'Software packages',
  devices: 'Devices',
  upfront: 'Up-front money & fees',
};

interface Props {
  journeyId: string | null;
  orderId: string;
  /** One per package, one per device model, one per fee. Penalties all 0. */
  lines: SettlementLine[];
  /**
   * This order's own units — read ONLY for the unwind checklist (which
   * serialized units are still `allocated`, which carry a rental agreement).
   * Optional: omit it and those two rows simply have nothing to list, which
   * is the honest state for a caller that has not loaded units yet.
   */
  units?: DeviceUnitRow[];
  /**
   * Fires whenever the cancellation record this panel is showing changes —
   * after every load and every mutation. The order screen has no other way
   * to know a cancellation exists: this panel owns the fetch, and the rail
   * (`OrderStages`) needs the same fact to know whether to draw the eight
   * `cx_*` stages at all.
   */
  onRecordChange?: (info: { cancellation: CancellationRecord | null; totals: SettlementTotals | null }) => void;
  /** Fires after a unit is released back to stock, so the order screen can reload its own unit list. */
  onUnitReleased?: (unitId: string) => void;
}

/** The five rows of the unwind checklist — see the `cx_unwind` tile in the mockup. */
const UNWIND_ROW_IDS = ['units', 'reservations', 'rentals', 'financed', 'merchant'] as const;
type UnwindRowId = (typeof UNWIND_ROW_IDS)[number];

const CancellationPanel: React.FC<Props> = ({ journeyId, orderId, lines, units, onRecordChange, onUnitReleased }) => {
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<CancellationRecord | null>(null);
  const [holds, setHolds] = useState<string[]>([]);
  /**
   * A FAILED READ IS NOT THE SAME FACT AS "NO CANCELLATION YET".
   *
   * A genuine 404 means nothing is open — the ordinary "Open a cancellation"
   * button is correct. Anything else (a 500, a dropped connection) must not
   * fall through to that same empty state: an admin reading it as "nothing
   * open" could open a SECOND cancellation on top of a live one the read
   * merely failed to fetch.
   */
  const [readError, setReadError] = useState<string | null>(null);
  const [exit, setExit] = useState<CancellationExit>('penalty');
  const [rows, setRows] = useState<SettlementLine[]>(lines);
  const [edited, setEdited] = useState<Record<string, true>>({});
  const [busy, setBusy] = useState(false);

  // Open-a-cancellation form (not open yet)
  const [origin, setOrigin] = useState<'customer' | 'rubytill' | null>(null);
  const [openReason, setOpenReason] = useState('');

  // In-page confirmation — never a native confirm().
  const [confirming, setConfirming] = useState<null | 'propose' | 'withdraw' | 'accept' | 'close'>(null);

  // The unwind checklist — five assertions a PERSON makes, ticked one at a
  // time. Reset on every fresh record so re-opening this order never inherits
  // a tick nobody actually re-checked this visit.
  const [unwindChecks, setUnwindChecks] = useState<Record<UnwindRowId, boolean>>({
    units: false, reservations: false, rentals: false, financed: false, merchant: false,
  });
  const [releasingUnit, setReleasingUnit] = useState<string | null>(null);
  /**
   * TRUE exactly when step 1 (cancel the order) succeeded and step 2 (close
   * the record) did not. The order IS cancelled at that point — never say
   * otherwise — and the only thing left to do is retry closing the record
   * alone. See `closeIt` / `retryCloseOnly`.
   */
  const [closePartial, setClosePartial] = useState(false);

  /** Guards state writes from a request that outlives this component — Retry included. */
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  const dataVersion = useDataVersion();
  /** Which journey has read successfully once — a re-read after that swaps data in place, no spinner. */
  const loadedFor = useRef<string | null>(null);
  /** The settlement version last copied into the editable exit/rows — a re-read of the SAME version keeps the admin's edits. */
  const appliedSettlement = useRef<string | null>(null);

  /**
   * `live` guards against a response landing after this effect's own cleanup
   * (journeyId changed, or the component unmounted) — never against a second
   * call while one is in flight, so `loadRecord` is also what Retry calls.
   */
  const loadRecord = useCallback((id: string, opts: { onLive: () => boolean }) => {
    if (loadedFor.current !== id) setLoading(true);
    setReadError(null);
    journeyApi.readCancellation(id)
      .then((r) => {
        if (!opts.onLive()) return;
        loadedFor.current = id;
        setRecord(r.cancellation);
        setHolds(r.holds || []);
        setReadError(null);
        const latest = r.cancellation.settlements[r.cancellation.settlements.length - 1];
        const key = latest ? `${id}:${latest.version}` : null;
        if (latest && key !== appliedSettlement.current) {
          appliedSettlement.current = key;
          setExit(latest.exit);
          setRows(latest.lines.length ? latest.lines : lines);
        }
      })
      .catch((e: any) => {
        if (!opts.onLive()) return;
        if (e?.status === 404) {
          loadedFor.current = id;
          // A genuine "nothing open on this order" — the ordinary empty state.
          setRecord(null);
          setHolds([]);
          setReadError(null);
        } else if (loadedFor.current === id) {
          // A failed background re-read keeps what is already on screen.
        } else {
          // Anything else is unknown, NOT "no cancellation". Do not let this
          // fall through to the open-a-cancellation button.
          setRecord(null);
          setHolds([]);
          setReadError(e?.message || 'Could not read this order\'s cancellation record.');
        }
      })
      .finally(() => { if (opts.onLive()) setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId]);

  useEffect(() => {
    if (!journeyId) { setLoading(false); return; }
    let live = true;
    loadRecord(journeyId, { onLive: () => live });
    return () => { live = false; };
  }, [journeyId, loadRecord, dataVersion]); // dataVersion: re-read after any save on the page

  // THE SAME FUNCTION THE SERVER USES. Never re-implement the maths here — a
  // screen that computed its own total would eventually disagree with the
  // settlement that was actually stored, and the screen is what people trust.
  //
  // Whole-branch review, Finding C1: `settlementTotals` returns a fresh
  // object literal on every call. A bare `const totals = settlementTotals(…)`
  // therefore gets a NEW IDENTITY on every render — and the effect below used
  // to key on that object, so it fired on every render, called
  // `onRecordChange` with a new object literal every time, which the parent's
  // `setCancellationSummary` never `Object.is`-bails on, which re-renders
  // this panel, which allocates a new `totals` again. Infinite loop; React
  // throws "Maximum update depth exceeded" on every hardware order page,
  // cancellation or not. `useMemo` keyed on the actual inputs fixes the
  // identity churn; the effect below is keyed on the primitive figures, not
  // the object, so it does not matter if some OTHER caller of this memo ever
  // stops being referentially stable.
  const totals = useMemo(() => settlementTotals({ exit, lines: rows }), [exit, rows]);

  /**
   * TELL THE ORDER SCREEN. This panel is the only thing that fetches the
   * cancellation record — the rail (`OrderStages`) needs to know whether one
   * exists at all, so it knows whether to draw the eight `cx_*` stages. Fires
   * after every load and every mutation, `record` being the single source of
   * truth either way.
   *
   * Keyed on `record` plus the primitive totals figures — NEVER on `totals`
   * itself. `totals` is an object; even memoized, keying an effect on an
   * object identity is fragile (see C1 above). The primitives are the only
   * things this effect actually cares about, and primitives compare by
   * value, so the effect only re-fires when a figure genuinely changed.
   */
  useEffect(() => {
    onRecordChange?.({ cancellation: record, totals: record ? totals : null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, totals.refundableCents, totals.penaltyCents, totals.netCents, totals.owedCents]);

  const setPenalty = (lineId: string, value: string) => {
    const cents = Math.round(parseFloat(value || '0') * 100);
    if (!Number.isFinite(cents) || cents < 0) return;
    setRows((rs) => rs.map((r) => (r.lineId === lineId ? { ...r, penaltyCents: cents } : r)));
    setEdited((e) => ({ ...e, [lineId]: true }));
  };

  const openIt = async () => {
    if (!journeyId || !origin) return;
    setBusy(true);
    try {
      const r = await journeyApi.openCancellation(journeyId, origin, openReason.trim() || undefined);
      setRecord(r.cancellation);
      setHolds(r.holds || []);
      setRows(lines);
      setEdited({});
      toastService.success('Cancellation opened — dispatch is held. Billing is not yet held automatically.');
    } catch (e: any) {
      toastService.error(e?.message || 'Could not open the cancellation.');
    } finally {
      setBusy(false);
    }
  };

  const propose = async () => {
    if (!journeyId) return;
    setBusy(true);
    try {
      const r = await journeyApi.saveSettlement(journeyId, { exit, lines: rows, propose: true });
      setRecord(r.cancellation);
      setHolds(r.holds || []);
      setConfirming(null);
      toastService.success(
        exit === 'solution' ? 'Counter-offer sent to the customer.' : 'Settlement proposed to the customer.',
      );
      /**
       * Whole-branch review, Finding I3: `cx_settlement` is a MANUAL rail
       * stage — `resolveJourney` only resolves it from `ctx.manual`, which
       * only `POST …/complete` writes. This settlement event landing on the
       * timeline is not the same fact as that. Without this call the rail
       * keeps showing "Settlement — waiting on a person" forever, even right
       * after a settlement was proposed, contradicting the timeline directly
       * above it. Best-effort, relative to the propose that already
       * succeeded: a failure here does not mean the settlement failed.
       */
      try {
        await journeyApi.complete(journeyId, 'cx_settlement');
      } catch (completeErr: any) {
        toastService.error(
          completeErr?.message
            || 'Settlement saved, but the rail could not be marked complete — it may still show as waiting.',
        );
      }
    } catch (e: any) {
      toastService.error(e?.message || 'Could not save that settlement.');
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    if (!journeyId) return;
    setBusy(true);
    try {
      const r = await journeyApi.withdrawCancellation(journeyId);
      setRecord(r.cancellation);
      setHolds(r.holds || []);
      setConfirming(null);
      // Reset the open-a-cancellation form so the NOT-OPEN view this now
      // reveals starts at the initial button, not resumed mid-way through
      // whatever origin was picked the last time it was open.
      setOrigin(null);
      setOpenReason('');
      toastService.info('Cancellation withdrawn — the order resumes.');
    } catch (e: any) {
      toastService.error(e?.message || 'Could not withdraw that cancellation.');
    } finally {
      setBusy(false);
    }
  };

  const retryRead = () => {
    if (!journeyId) return;
    // Guarded the same way the initial load is — a slow retry that outlives
    // this component (navigated away, order screen closed) must not write
    // state into an unmounted component.
    loadRecord(journeyId, { onLive: () => mountedRef.current });
  };

  /** Records that the customer accepted the standing settlement. Opens the unwind checklist next. */
  const acceptIt = async () => {
    if (!journeyId) return;
    setBusy(true);
    try {
      const r = await journeyApi.acceptCancellation(journeyId);
      setRecord(r.cancellation);
      setHolds(r.holds || []);
      setConfirming(null);
      // A fresh checklist for a fresh acceptance — nobody's tick from a
      // different visit should count for this one.
      setUnwindChecks({ units: false, reservations: false, rentals: false, financed: false, merchant: false });
      toastService.success('Cancellation accepted. Work through the unwind checklist before this can close.');
    } catch (e: any) {
      toastService.error(e?.message || 'Could not mark this cancellation accepted.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * ONE SERIALIZED UNIT, released back to stock — the same status change a
   * warehouse operator makes from the unit screen. `orders.ts:255-258` never
   * does this on its own, which is the whole reason this row exists.
   */
  const releaseUnit = async (unitId: string) => {
    setReleasingUnit(unitId);
    try {
      await hardwareApi.setUnitStatus(unitId, { state: 'in_stock' });
      toastService.success('Unit released back to stock.');
      onUnitReleased?.(unitId);
    } catch (e: any) {
      toastService.error(e?.message || 'Could not release that unit.');
    } finally {
      setReleasingUnit(null);
    }
  };

  /**
   * Whole-branch review, Finding I3: `cx_unwind` is the OTHER manual rail
   * stage, and the five checklist rows above ARE the human assertion it
   * represents — a person saying they checked. Until now that tick lived
   * only in `unwindChecks` component state and was thrown away the moment
   * this panel unmounted; the rail never learned it happened, so it kept
   * showing "Unwind — waiting on a person" under a closed cancellation,
   * contradicting the timeline. Best-effort relative to the close that
   * already succeeded — if this call fails, say so, but never claim the
   * close itself failed or roll anything back.
   */
  const completeUnwindStage = async () => {
    if (!journeyId) return;
    const rowLabel: Record<UnwindRowId, string> = {
      units: 'serialized units', reservations: 'quantity reservations',
      rentals: 'rented devices', financed: 'financed balance', merchant: 'merchant application',
    };
    const note = `Unwind checklist ticked by a person: ${UNWIND_ROW_IDS.map((id) => rowLabel[id]).join(', ')}.`;
    try {
      await journeyApi.complete(journeyId, 'cx_unwind', note);
    } catch (completeErr: any) {
      toastService.error(
        completeErr?.message
          || 'The order is closed, but the rail could not be marked complete — it may still show as waiting.',
      );
    }
  };

  /**
   * CLOSE — two calls, two services, and the order between them is NOT
   * negotiable.
   *
   * 1. Cancel the order itself, carrying this cancellation's own id so the
   *    server can tell a deliberate close apart from the legacy "just cancel
   *    it" button and a stale tab holding an old id.
   * 2. ONLY if that succeeded: close the cancellation record, which is what
   *    actually releases the dispatch/billing hold.
   *
   * The reverse is unsafe — closing first releases the hold, so a failed
   * cancel afterwards would let an un-cancelled order resume shipping.
   * Cancelling first means a failed close leaves a cancelled order with a
   * stale hold, which costs nothing: a cancelled order is already refused
   * everywhere else in the system.
   */
  const closeIt = async () => {
    if (!journeyId || !record) return;
    setBusy(true);
    setClosePartial(false);
    try {
      await hardwareApi.cancelOrder(orderId, { viaCancellationId: record.cancellationId });
    } catch (e: any) {
      // Step 1 failed. Stop here — step 2 is never called, and nothing has
      // changed: the cancellation record is exactly as it was.
      toastService.error(e?.message || 'Could not cancel the order — nothing closed.');
      setBusy(false);
      return;
    }
    try {
      const r = await journeyApi.closeCancellation(journeyId);
      setRecord(r.cancellation);
      setHolds(r.holds || []);
      setConfirming(null);
      toastService.success('The order is cancelled and this cancellation is closed.');
      await completeUnwindStage();
    } catch (e: any) {
      // Step 1 succeeded, step 2 did not. The order IS cancelled — say so
      // plainly, never as though nothing happened — and offer to retry only
      // the step that failed.
      setClosePartial(true);
      toastService.error('The order is cancelled, but the cancellation record did not close. Retry closing the record below.');
    } finally {
      setBusy(false);
    }
  };

  /** The order is already cancelled — retry ONLY the record close, never step 1 again. */
  const retryCloseOnly = async () => {
    if (!journeyId) return;
    setBusy(true);
    try {
      const r = await journeyApi.closeCancellation(journeyId);
      setRecord(r.cancellation);
      setHolds(r.holds || []);
      setClosePartial(false);
      setConfirming(null);
      toastService.success('The cancellation record is now closed.');
      await completeUnwindStage();
    } catch (e: any) {
      toastService.error(e?.message || 'Still could not close the record. The order remains cancelled either way.');
    } finally {
      setBusy(false);
    }
  };

  if (!journeyId) return null;

  if (loading) {
    return (
      <RubyLoader variant="section" label="reading the cancellation record…" className="rounded-2xl border border-stone-200 bg-white" />
    );
  }

  // ── READ FAILED — NOT the same fact as "nothing open" ───────────────────
  // Must never fall through to the open-a-cancellation button: an admin
  // reading a failed fetch as "no cancellation" could open a duplicate on
  // top of a live one this screen simply could not reach.
  if (readError) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
        <p className="text-[11px] uppercase tracking-[0.12em] font-bold text-amber-700 mb-2">Cancellation</p>
        <div className="flex items-start gap-2.5 text-[12.5px] text-amber-900">
          <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-600" />
          <div>
            <p><b>Could not read the cancellation record for this order.</b> {readError}</p>
            <p className="text-[11.5px] text-amber-700 mt-1">
              This is not the same as "no cancellation open" — do not open a new one until this loads.
            </p>
          </div>
        </div>
        <button
          onClick={retryRead}
          className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-amber-300 bg-white text-amber-800 text-xs font-semibold min-h-[44px] hover:bg-amber-100"
        >
          <RotateCcw size={13} /> Retry
        </button>
      </div>
    );
  }

  const isLive = !!record && record.status !== 'withdrawn' && record.status !== 'closed';

  /**
   * ── CLOSED — THE TERMINAL SCREEN ─────────────────────────────────────────
   * Whole-branch review, Finding I4: `isLive` excludes `closed` on purpose
   * (a closed cancellation is not live), but nothing used to catch that case
   * on its own — it fell all the way through to the "Open a cancellation"
   * empty state, indistinguishable from an order that never had one.
   * Clicking that button called `openCancellation`, which is idempotent and
   * hands back the closed record unchanged, so nothing visibly happened.
   * Read-only, no actions: the order is already cancelled and this record is
   * terminal — there is nothing left here to do.
   */
  if (record && record.status === 'closed') {
    return (
      <div className="rounded-2xl border border-stone-200 bg-white p-4 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11px] uppercase tracking-[0.12em] font-bold text-stone-500">Cancellation</p>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-stone-100 text-stone-600 text-[10.5px] font-bold">
            <CheckCircle2 size={12} /> Closed
          </span>
        </div>

        <div className="flex items-start gap-2.5 rounded-xl border border-stone-200 bg-stone-50 px-3.5 py-3 text-[12.5px] text-stone-700">
          <Ban size={16} className="shrink-0 text-stone-400 mt-0.5" />
          <div>
            <p><b>This order is cancelled.</b> The cancellation was {record.origin === 'customer' ? 'raised by the customer' : 'raised by RubyTill'}
              {record.closedAt ? ` and closed ${new Date(record.closedAt).toLocaleString()}` : ''}.
            </p>
            {record.acceptedAt && (
              <p className="text-[11.5px] text-stone-500 mt-1">
                Settlement v{record.acceptedSettlementVersion ?? '?'} accepted {new Date(record.acceptedAt).toLocaleString()}.
              </p>
            )}
          </div>
        </div>

        {/* the settlement as it stood at acceptance — read-only, same settlementTotals output as every other screen */}
        <div className="rounded-xl border border-stone-200 grid grid-cols-2 sm:grid-cols-4">
          <div className="p-3 border-r border-b sm:border-b-0 border-stone-200">
            <div className="text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500">Refundable</div>
            <div className="text-[15px] font-bold text-emerald-700 mt-0.5">{money(totals.refundableCents)}</div>
          </div>
          <div className="p-3 border-r border-b sm:border-b-0 border-stone-200">
            <div className="text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500">Penalty</div>
            <div className="text-[15px] font-bold text-rose-700 mt-0.5">{money(totals.penaltyCents)}</div>
          </div>
          <div className="p-3 border-r border-stone-200">
            <div className="text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500">Net to customer</div>
            <div className={`text-[15px] font-bold mt-0.5 ${totals.netCents >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{money(totals.netCents)}</div>
          </div>
          <div className="p-3">
            <div className="text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500">Still owed to us</div>
            <div className="text-[15px] font-bold text-rose-700 mt-0.5">{money(totals.owedCents)}</div>
          </div>
        </div>

        <p className="text-[10.5px] text-stone-400">
          Order {orderId} · this cancellation is closed and terminal — it cannot be reopened, and no new cancellation
          can be opened on top of it.
        </p>
      </div>
    );
  }

  // ── NOT OPEN YET ─────────────────────────────────────────────────────────
  if (!isLive) {
    return (
      <div className="rounded-2xl border border-stone-200 bg-white p-4">
        <p className="text-[11px] uppercase tracking-[0.12em] font-bold text-stone-500 mb-3">Cancellation</p>
        {!origin ? (
          <button
            onClick={() => setOrigin('customer')}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-ruby text-ruby text-xs font-semibold min-h-[44px] hover:bg-rose-50"
          >
            <Ban size={13} /> Open a cancellation
          </button>
        ) : (
          <div className="space-y-3">
            <p className="text-[12.5px] text-stone-600">Who is raising this?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                onClick={() => setOrigin('customer')}
                className={`text-left rounded-xl border p-3 text-[12.5px] ${origin === 'customer' ? 'border-ruby bg-rose-50' : 'border-stone-200 bg-white hover:bg-stone-50'}`}
              >
                <div className="font-semibold text-stone-800">The customer asked</div>
                <div className="text-[11px] text-stone-500 mt-0.5">A request, never a cancel — from their portal or a call.</div>
              </button>
              <button
                onClick={() => setOrigin('rubytill')}
                className={`text-left rounded-xl border p-3 text-[12.5px] ${origin === 'rubytill' ? 'border-ruby bg-rose-50' : 'border-stone-200 bg-white hover:bg-stone-50'}`}
              >
                <div className="font-semibold text-stone-800">We are initiating</div>
                <div className="text-[11px] text-stone-500 mt-0.5">Non-payment, fraud, a store that never opened.</div>
              </button>
            </div>
            <input
              value={openReason}
              onChange={(e) => setOpenReason(e.target.value)}
              placeholder="Reason (optional) — shown on the conversation thread"
              className="w-full px-3.5 py-2.5 rounded-lg border border-stone-200 text-[12.5px] text-stone-800 focus:outline-none focus:border-ruby focus:ring-2 focus:ring-ruby/10"
            />
            <div className="flex gap-2">
              <button
                onClick={openIt}
                disabled={busy}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-ruby hover:bg-ruby-deep text-white text-xs font-semibold min-h-[44px] disabled:opacity-50"
              >
                {busy ? <RubyGemMark size={13} drawing color="currentColor" /> : <Ban size={13} />}
                Open the cancellation
              </button>
              <button
                onClick={() => { setOrigin(null); setOpenReason(''); }}
                disabled={busy}
                className="px-4 py-2.5 rounded-lg border border-stone-200 text-stone-600 text-xs font-semibold min-h-[44px] hover:bg-stone-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── ACCEPTED — THE UNWIND CHECKLIST, THEN CLOSE ─────────────────────────
  // Design source: the `cx_unwind` tile in mockups/order-cancellation-refund.html.
  // Five things the cancellation does not do by itself. Close stays refused
  // until a person has ticked every one — these are assertions BY a person,
  // never facts the system verified, because for four of the five there is
  // no fact here to read.
  if (record.status === 'accepted') {
    const allocatedUnits = (units || []).filter((u) => u.state === 'allocated');
    const rentalUnits = (units || []).filter((u) => u.agreement?.saleMode === 'rental');
    const allTicked = UNWIND_ROW_IDS.every((id) => unwindChecks[id]);

    const checkRow = (id: UnwindRowId, name: string, meta: React.ReactNode, extra?: React.ReactNode) => (
      <label
        key={id}
        className="flex items-start gap-3 rounded-xl border border-stone-200 bg-white p-3.5 cursor-pointer hover:border-stone-300"
      >
        <input
          type="checkbox"
          checked={unwindChecks[id]}
          onChange={(e) => setUnwindChecks((c) => ({ ...c, [id]: e.target.checked }))}
          className="mt-1 w-[18px] h-[18px] accent-ruby shrink-0"
        />
        <div className="flex-1 min-w-0">
          <p className="text-[12.5px] font-bold text-stone-900">{name}</p>
          <p className="text-[11px] text-stone-500 mt-0.5 leading-relaxed">{meta}</p>
          {extra}
        </div>
      </label>
    );

    return (
      <div className="rounded-2xl border border-ruby/30 bg-white p-4 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11px] uppercase tracking-[0.12em] font-bold text-stone-500">Cancellation settlement</p>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 text-[10.5px] font-bold">
            {record?.origin === 'customer' ? 'Raised by the customer' : 'Raised by RubyTill'} · {record?.status}
          </span>
        </div>

        {holds.length > 0 && (
          <div className="flex items-center gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-3 text-[12.5px] text-rose-800">
            <ShieldAlert size={16} className="shrink-0 text-rose-600" />
            <span><b>Dispatch is still held.</b> Nothing ships until this closes.</span>
          </div>
        )}

        {/* the accepted settlement, read-only — every figure is settlementTotals output, nothing recomputed here */}
        <div className="rounded-xl border border-stone-200 grid grid-cols-2 sm:grid-cols-4">
          <div className="p-3 border-r border-b sm:border-b-0 border-stone-200">
            <div className="text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500">Refundable</div>
            <div className="text-[15px] font-bold text-emerald-700 mt-0.5">{money(totals.refundableCents)}</div>
          </div>
          <div className="p-3 border-r border-b sm:border-b-0 border-stone-200">
            <div className="text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500">Penalty</div>
            <div className="text-[15px] font-bold text-rose-700 mt-0.5">{money(totals.penaltyCents)}</div>
          </div>
          <div className="p-3 border-r border-stone-200">
            <div className="text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500">Net to customer</div>
            <div className={`text-[15px] font-bold mt-0.5 ${totals.netCents >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{money(totals.netCents)}</div>
          </div>
          <div className="p-3">
            <div className="text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500">Still owed to us</div>
            <div className="text-[15px] font-bold text-rose-700 mt-0.5">{money(totals.owedCents)}</div>
          </div>
        </div>

        {/* the unwind checklist */}
        <div>
          <p className="text-[11px] uppercase tracking-[0.1em] font-bold text-stone-500 mb-1 flex items-center gap-1.5">
            <PackageOpen size={13} className="text-stone-400" /> Unwinding — 5 things the cancellation does not do by itself
          </p>
          <p className="text-[11.5px] text-stone-500 mb-2.5 leading-relaxed">
            Cancelling the order marks it cancelled and stops. Everything below is left behind — silently. None of
            these checkboxes is a fact the system verified; each one is a person saying they checked.
          </p>
          <div className="space-y-2">
            {checkRow(
              'units',
              `Serialized units still allocated (${allocatedUnits.length})`,
              <>
                <span className="font-mono text-stone-400">orders.ts:255-258</span> says it outright: serialized units are not
                returned to stock when an order cancels. They stay allocated to this order until a person moves
                each one — the button below is the same status change a warehouse operator makes.
              </>,
              <div className="mt-2 space-y-1.5">
                {allocatedUnits.map((u) => (
                  <div key={u.unitId} className="flex items-center justify-between gap-2 rounded-lg border border-stone-200 px-2.5 py-1.5">
                    <span className="text-[11px] text-stone-700 truncate">{u.trackingId || u.serialNumber || u.unitId} · {u.modelId}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); releaseUnit(u.unitId); }}
                      disabled={releasingUnit === u.unitId}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-ruby text-ruby text-[10.5px] font-semibold hover:bg-rose-50 disabled:opacity-50 shrink-0"
                    >
                      {releasingUnit === u.unitId ? <RubyGemMark size={11} drawing color="currentColor" /> : <CheckCircle2 size={11} />}
                      Release to stock
                    </button>
                  </div>
                ))}
                {allocatedUnits.length === 0 && (
                  <p className="text-[11px] text-stone-400">No units currently show as allocated to this order.</p>
                )}
              </div>,
            )}

            {checkRow(
              'reservations',
              'Quantity reservations',
              <>
                Released best-effort, inside a try/catch that only logs (<span className="font-mono text-stone-400">orders.ts:265-280</span>).
                A failed release leaves phantom stock and nobody is told — there is no button here because there
                is nothing this screen can verify.
              </>,
            )}

            {checkRow(
              'rentals',
              `Rented devices awaiting return (${rentalUnits.length})`,
              <>
                <span className="font-mono text-stone-400">payments.ts · cancelRentalAgreements</span> sets a cancelled status and
                a return-expected date, writes an audit row, and stops there. Nothing chases the date — it is a
                separate, billing-side flow this order cancellation never calls.
              </>,
              rentalUnits.length > 0 && (
                <div className="mt-2 space-y-1">
                  {rentalUnits.map((u) => (
                    <p key={u.unitId} className="text-[11px] text-stone-600">
                      {u.trackingId || u.serialNumber || u.unitId} · {u.modelId}
                      {u.returnExpectedAt ? ` · expected back ${new Date(u.returnExpectedAt).toLocaleDateString()}` : ' · no return date on file'}
                    </p>
                  ))}
                </div>
              ),
            )}

            {checkRow(
              'financed',
              'Financed balance',
              <>
                No code computes or charges this balance — <span className="font-mono text-stone-400">settlementTotals</span> reports
                it as {money(totals.owedCents)} because nothing has ever written a non-zero figure here. Shown, never charged.
              </>,
            )}

            {checkRow(
              'merchant',
              'The merchant application',
              <>
                There is no withdrawal endpoint. If an application for this tenant is mid-flight with a processor,
                cancelling this order does not touch it — a TID could still be issued against a store that is closing.
              </>,
            )}
          </div>
        </div>

        {closePartial && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-3.5 text-[12px] text-amber-900">
            <p><b>The order is cancelled — that part is done.</b> The cancellation record itself did not close. The
              order needs nothing further from you; only the record does.</p>
            <button
              onClick={retryCloseOnly}
              disabled={busy}
              className="mt-2.5 inline-flex items-center gap-2 px-3.5 py-2.5 rounded-lg bg-ruby hover:bg-ruby-deep text-white text-[11.5px] font-semibold min-h-[44px] disabled:opacity-50"
            >
              {busy ? <RubyGemMark size={12} drawing color="currentColor" /> : <RotateCcw size={12} />}
              Retry closing the record
            </button>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <button
            onClick={() => setConfirming('close')}
            disabled={busy || !allTicked || closePartial}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-ruby hover:bg-ruby-deep text-white text-xs font-semibold min-h-[44px] disabled:opacity-50"
          >
            Cancel the order and close
          </button>
          <button
            onClick={() => setConfirming('withdraw')}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-stone-200 text-stone-600 text-xs font-semibold min-h-[44px] hover:bg-stone-50 disabled:opacity-50"
          >
            Withdraw the cancellation
          </button>
        </div>
        {!allTicked && !closePartial && (
          <p className="text-[11px] text-stone-400">Tick every row above before this can close.</p>
        )}

        {confirming && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-3.5">
            <div className="text-[12.5px] font-bold text-rose-800 flex items-center gap-1.5">
              <AlertTriangle size={14} />
              {confirming === 'withdraw' ? 'Withdraw this cancellation?' : 'Cancel this order and close the cancellation?'}
            </div>
            <p className="text-[11.5px] text-rose-700 mt-1.5 mb-3 leading-relaxed">
              {confirming === 'withdraw'
                ? 'The order resumes immediately — dispatch un-holds and every held stage continues where it stopped.'
                : 'This actually cancels the order — irreversible from here. It runs as two calls: cancel the order, then close this record. If the order cancels but the record fails to close, the order stays cancelled and you will be offered a retry for the record alone.'}
            </p>
            <div className="flex gap-2">
              <button
                onClick={confirming === 'withdraw' ? withdraw : closeIt}
                disabled={busy}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-ruby hover:bg-ruby-deep text-white text-[11.5px] font-semibold disabled:opacity-50"
              >
                {busy ? <RubyGemMark size={12} drawing color="currentColor" /> : null}
                {confirming === 'withdraw' ? 'Yes, withdraw it' : 'Yes, cancel and close'}
              </button>
              <button
                onClick={() => setConfirming(null)}
                disabled={busy}
                className="px-3.5 py-2 rounded-lg border border-stone-200 bg-white text-stone-600 text-[11.5px] font-semibold hover:bg-stone-50 disabled:opacity-50"
              >
                Not yet
              </button>
            </div>
          </div>
        )}
        <p className="text-[10.5px] text-stone-400">Order {orderId} · no native dialog is used anywhere on this panel.</p>
      </div>
    );
  }

  // ── OPEN — THE WORKSHEET ────────────────────────────────────────────────
  const disabled = exit === 'solution' || exit === 'forgive';
  let lastGroup = '';

  return (
    <div className="rounded-2xl border border-ruby/30 bg-white p-4 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[11px] uppercase tracking-[0.12em] font-bold text-stone-500">Cancellation settlement</p>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 text-[10.5px] font-bold">
          {record?.origin === 'customer' ? 'Raised by the customer' : 'Raised by RubyTill'} · {record?.status}
        </span>
      </div>

      {holds.length > 0 && (
        <div className="flex items-center gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-3 text-[12.5px] text-rose-800">
          <ShieldAlert size={16} className="shrink-0 text-rose-600" />
          <span><b>Dispatch is held</b> — nothing ships until this settlement closes. Billing is not yet held automatically: the subscription keeps billing as normal until that is built.</span>
        </div>
      )}

      {/* worksheet */}
      <div className="rounded-xl border border-stone-200 overflow-hidden">
        {/*
          WHY THE PENALTY INPUTS ARE DISABLED UNDER `forgive` — visible inline
          text, never a hover-only tooltip, because a rule that changes what a
          disabled control means has to be seen without hovering over it.
          `settlementTotals` zeroes penalty unconditionally for this exit
          (`solution || forgive ? 0 : l.penaltyCents`) — a per-line override
          would look live and change nothing, which is worse than disabling
          it. Charging some lines while forgiving the rest is a real thing an
          admin can still do — just not from this exit.
        */}
        {exit === 'forgive' && (
          <div className="flex items-start gap-2.5 border-b border-stone-200 bg-slate-50 px-3.5 py-3 text-[12px] text-slate-700">
            <span className="font-bold text-slate-400 mt-px">ⓘ</span>
            <span>
              <b>Every penalty is zero under "Forgive everything pending"</b> — the inputs below are disabled
              because there is nothing to type here; this exit writes off every line, no exceptions. To charge
              a penalty on some lines while forgiving the rest, switch to <b>Charge a penalty</b> below and zero
              out the lines you want to forgive instead.
            </span>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[660px] text-[12px] border-collapse">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-200">
                <th className="text-left text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500 px-3 py-2.5">Line</th>
                <th className="text-right text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500 px-3 py-2.5">Paid to date<sup className="text-stone-400">†</sup></th>
                <th className="text-left text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500 px-3 py-2.5">Rule</th>
                <th className="text-right text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500 px-3 py-2.5">Refundable<sup className="text-stone-400">†</sup></th>
                <th className="text-right text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500 px-3 py-2.5">Penalty</th>
                <th className="text-right text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500 px-3 py-2.5">Net</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => {
                const showGroup = l.group !== lastGroup;
                lastGroup = l.group;
                // The row's own net, from the SAME function run on just this one
                // line — never a hand-rolled refundable-minus-penalty here.
                const lineNet = settlementTotals({ exit, lines: [l] }).netCents;
                return (
                  <React.Fragment key={l.lineId}>
                    {showGroup && (
                      <tr className="bg-stone-50/70">
                        <td colSpan={6} className="px-3 py-1.5 text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500">
                          {GROUP_LABEL[l.group] || l.group}
                        </td>
                      </tr>
                    )}
                    <tr className="border-b border-stone-100 last:border-0">
                      <td className="px-3 py-2.5 align-middle">
                        <div className="font-semibold text-stone-900">{l.label}</div>
                        {l.detail && <div className="text-[10.5px] text-stone-500 mt-0.5">{l.detail}</div>}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-stone-700">{money(l.paidCents)}</td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10.5px] font-bold bg-white text-stone-400 border border-dashed border-stone-300">
                          {l.ruleLabel}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-stone-700">
                        {exit === 'solution' ? <span className="text-stone-300">—</span> : money(l.refundableCents)}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <input
                          value={l.penaltyCents / 100}
                          onChange={(ev) => setPenalty(l.lineId, ev.target.value)}
                          disabled={disabled}
                          type="number"
                          min={0}
                          step="0.01"
                          className={`w-[78px] text-right px-2 py-1 rounded-md border text-[11.5px] font-mono disabled:opacity-50 disabled:bg-stone-50 ${
                            edited[l.lineId] ? 'border-ruby bg-rose-50' : 'border-stone-200'
                          }`}
                        />
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono font-semibold text-stone-900">{money(lineNet)}</td>
                    </tr>
                  </React.Fragment>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-[12px] text-stone-400">No settlement lines on this order.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* totals — every one of these is settlementTotals output, nothing recomputed here */}
        <div className="grid grid-cols-2 sm:grid-cols-4 border-t border-stone-200">
          <div className="p-3 border-r border-stone-200">
            <div className="text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500">Refundable</div>
            <div className="text-[16px] font-bold text-emerald-700 mt-0.5">{money(totals.refundableCents)}</div>
          </div>
          <div className="p-3 border-r border-stone-200 sm:border-r">
            <div className="text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500">Penalty</div>
            <div className="text-[16px] font-bold text-rose-700 mt-0.5">{money(totals.penaltyCents)}</div>
          </div>
          <div className="p-3 border-r border-stone-200">
            <div className="text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500">Net to customer</div>
            <div className={`text-[16px] font-bold mt-0.5 ${totals.netCents >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{money(totals.netCents)}</div>
          </div>
          <div className="p-3">
            <div className="text-[9.5px] uppercase tracking-[0.1em] font-bold text-stone-500">Still owed to us</div>
            <div className="text-[16px] font-bold text-rose-700 mt-0.5">{money(totals.owedCents)}</div>
          </div>
        </div>
      </div>

      {/*
        THE ESTIMATE DISCLOSURE — same register as the mockup's own
        "⚠ balance not computed anywhere" flag: honest and specific, not
        alarming. `paidCents` / `refundableCents` on every line above are
        `amountCents × quantity` from the order — the order's list price, not
        a per-payment ledger, which this screen has no access to. For a
        financed device that overstates what has actually been paid, so this
        says so out loud rather than let the numbers read as fact.
      */}
      <div className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-[12px] text-slate-700">
        <span className="font-bold text-slate-400 mt-px">†</span>
        <span>
          <b>Paid to date and Refundable are estimated</b> from the order's list price (quantity × unit price) —
          this screen has no per-payment ledger to read instead. For a financed device this is the full order
          value, not what has actually been collected so far. Check the real amount before relying on it.
        </span>
      </div>

      {/* the three exits */}
      <div>
        <p className="text-[11px] uppercase tracking-[0.1em] font-bold text-stone-500 mb-2">The three ways out — before anything is cancelled</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
          {CANCELLATION_EXITS.map((x) => {
            const on = exit === x;
            const copy = EXIT_COPY[x];
            // Each card quotes settlementTotals for ITS OWN exit — the same
            // function, run once per candidate, never a separate formula.
            const preview = settlementTotals({ exit: x, lines: rows });
            return (
              <button
                key={x}
                onClick={() => { setExit(x); setConfirming(null); }}
                className={`text-left rounded-xl border-[1.5px] p-3.5 transition-colors ${on ? 'border-ruby bg-rose-50/60' : 'border-stone-200 bg-white hover:border-stone-300'}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-3.5 h-3.5 rounded-full border-2 flex-none ${on ? 'border-ruby bg-ruby' : 'border-stone-300'}`} />
                  <span className="text-[13px] font-bold text-stone-900">{copy.title}</span>
                </div>
                <p className="text-[11px] text-stone-500 mt-1 leading-relaxed">{copy.desc}</p>
                <p className={`text-[14px] font-bold mt-2 ${preview.netCents >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {x === 'solution' ? '$0.00 moves' : `${money(preview.netCents)} to them`}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* actions */}
      <div className="flex flex-wrap gap-2 pt-1">
        <button
          onClick={() => setConfirming('propose')}
          disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-ruby hover:bg-ruby-deep text-white text-xs font-semibold min-h-[44px] disabled:opacity-50"
        >
          {exit === 'solution' ? 'Send the counter-offer' : 'Propose to the customer'}
        </button>
        {/* Only once something has actually been proposed — `acceptCancellation`
            refuses with a 409 until the latest settlement carries a `proposedAt`. */}
        {record?.status === 'proposed' && (
          <button
            onClick={() => setConfirming('accept')}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-ruby hover:bg-ruby-deep text-white text-xs font-semibold min-h-[44px] disabled:opacity-50"
          >
            Mark accepted
          </button>
        )}
        <button
          onClick={() => setConfirming('withdraw')}
          disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-stone-200 text-stone-600 text-xs font-semibold min-h-[44px] hover:bg-stone-50 disabled:opacity-50"
        >
          Withdraw the cancellation
        </button>
      </div>

      {/* in-page confirmation — never a native confirm() */}
      {confirming && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3.5">
          <div className="text-[12.5px] font-bold text-rose-800 flex items-center gap-1.5">
            <AlertTriangle size={14} />
            {confirming === 'withdraw'
              ? 'Withdraw this cancellation?'
              : confirming === 'accept'
                ? 'Mark this cancellation accepted?'
                : exit === 'solution' ? 'Send this counter-offer to the customer?' : 'Propose this settlement to the customer?'}
          </div>
          <p className="text-[11.5px] text-rose-700 mt-1.5 mb-3 leading-relaxed">
            {confirming === 'withdraw'
              ? 'The order resumes immediately — dispatch un-holds and every held stage continues where it stopped.'
              : confirming === 'accept'
                ? 'Records that the customer accepted the standing settlement. Nothing is charged, refunded or cancelled yet — that is the close step, once the unwind checklist is done.'
                : exit === 'solution'
                  ? 'Nothing is cancelled and nothing is refunded. This records the offer on the cancellation thread — the cancellation stays open, and dispatch stays held, until someone withdraws it.'
                  : `They see ${money(totals.refundableCents)} refundable and ${money(totals.penaltyCents)} penalty. Nothing is charged, refunded or cancelled until they accept.`}
          </p>
          <div className="flex gap-2">
            <button
              onClick={confirming === 'withdraw' ? withdraw : confirming === 'accept' ? acceptIt : propose}
              disabled={busy}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-ruby hover:bg-ruby-deep text-white text-[11.5px] font-semibold disabled:opacity-50"
            >
              {busy ? <RubyGemMark size={12} drawing color="currentColor" /> : null}
              {confirming === 'withdraw' ? 'Yes, withdraw it' : confirming === 'accept' ? 'Yes, mark accepted' : 'Yes, send it'}
            </button>
            <button
              onClick={() => setConfirming(null)}
              disabled={busy}
              className="px-3.5 py-2 rounded-lg border border-stone-200 bg-white text-stone-600 text-[11.5px] font-semibold hover:bg-stone-50 disabled:opacity-50"
            >
              Not yet
            </button>
          </div>
        </div>
      )}
      <p className="text-[10.5px] text-stone-400">Order {orderId} · no native dialog is used anywhere on this panel.</p>
    </div>
  );
};

export default CancellationPanel;
